/**
 * Public signup API helpers (onboarding intent, BrasilAPI CNPJ, status polling).
 *
 * Status (`GET /signup/status`) serializes the body directly and puts human
 * copy in `message` (string). Do not treat a string `message` as a wrapper.
 */

const SIGNUP_JSON_HEADERS = { "Content-Type": "application/json" };

/**
 * @param {unknown} payload
 * @returns {Record<string, unknown>}
 */
export function unwrapSignupJson(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }
  const message = payload.message;
  if (message && typeof message === "object" && !Array.isArray(message)) {
    return message;
  }
  return /** @type {Record<string, unknown>} */ (payload);
}

/**
 * @param {Record<string, unknown>} body
 * @returns {Promise<string>} onboarding_id
 */
export async function createOnboardingIntent(body) {
  const response = await fetch(`${window.CONFIG.API_BASE_URL}/signup/onboarding-intent`, {
    method: "POST",
    headers: SIGNUP_JSON_HEADERS,
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  const data = unwrapSignupJson(payload);
  if (!response.ok) {
    throw new Error(
      String(data?.error || "Não foi possível registrar os dados.")
    );
  }
  const onboardingId = String(data?.onboarding_id || "").trim();
  if (!onboardingId) {
    throw new Error("Resposta do servidor sem identificador de cadastro.");
  }
  return onboardingId;
}

/**
 * BrasilAPI CNPJ enrichment (BR-only). Returns null on 404 / network failure.
 * @param {string} cleanedTaxId digits-only CNPJ
 * @returns {Promise<Record<string, unknown>|null>}
 */
export async function lookupBrasilApiCnpj(cleanedTaxId) {
  const cleaned = String(cleanedTaxId || "").replace(/\D/g, "");
  if (!cleaned) return null;
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleaned}`, {
      method: "GET",
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new Error("Consulta CNPJ indisponível");
    }
    return /** @type {Record<string, unknown>} */ (await res.json());
  } catch (error) {
    window.Logger?.warn?.("BrasilAPI CNPJ lookup failed", error);
    return null;
  }
}

/**
 * @param {string} sessionId
 * @returns {Promise<string>} onboarding_id (may be empty)
 */
export async function resolveCheckoutSession(sessionId) {
  const id = String(sessionId || "").trim();
  if (!id || !window.CONFIG?.API_BASE_URL) return "";
  try {
    const res = await fetch(
      `${window.CONFIG.API_BASE_URL}/signup/resolve-checkout-session?session_id=${encodeURIComponent(id)}`,
      { method: "GET", headers: SIGNUP_JSON_HEADERS }
    );
    const payload = await res.json().catch(() => null);
    const data = unwrapSignupJson(payload);
    return String(data?.onboarding_id || "").trim();
  } catch (error) {
    window.Logger?.warn?.("Could not resolve checkout session to onboarding id", error);
    return "";
  }
}

/**
 * @param {string} onboardingId
 * @returns {Promise<{ ok: boolean, statusCode: number, data: Record<string, unknown> }>}
 */
export async function fetchSignupStatus(onboardingId) {
  const id = String(onboardingId || "").trim();
  if (!id || !window.CONFIG?.API_BASE_URL) {
    return { ok: false, statusCode: 0, data: {} };
  }
  try {
    const response = await fetch(
      `${window.CONFIG.API_BASE_URL}/signup/status/${encodeURIComponent(id)}`,
      { method: "GET", headers: SIGNUP_JSON_HEADERS }
    );
    if (response.status === 404) {
      return { ok: false, statusCode: 404, data: {} };
    }
    const payload = await response.json().catch(() => null);
    return {
      ok: response.ok,
      statusCode: response.status,
      data: unwrapSignupJson(payload),
    };
  } catch (error) {
    window.Logger?.warn?.("Could not refresh signup status", error);
    return { ok: false, statusCode: 0, data: {} };
  }
}
